import { useCallback, useEffect } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useRecoilState } from 'recoil';
import { DEFAULT_CONTEXT_DRAWER_TAB, CONTEXT_DRAWER_TABS } from '../constants/contextDrawerTabs';
import { interveneContextDrawerAtom } from '../state/interveneAtoms';

function resolveContextDrawerTab(tab) {
  const validTabs = Object.values(CONTEXT_DRAWER_TABS);
  if (typeof tab === 'string' && validTabs.includes(tab)) {
    return tab;
  }
  return DEFAULT_CONTEXT_DRAWER_TAB;
}

/**
 * Controls customer context drawer visibility and active tab.
 * - Closed by default and opened only for a selected conversation
 * - State survives child remounts and transient profile refreshes
 * - Inline panel on md+, overlay Drawer on mobile
 */
export default function useCustomerContextDrawer(activeUserPhone) {
  const [drawerState, setDrawerState] = useRecoilState(interveneContextDrawerAtom);
  const { isOpen, activeTab } = drawerState;
  const theme = useTheme();
  const isInlineLayout = useMediaQuery(theme.breakpoints.up('md'));

  const open = useCallback((tab) => {
    if (!activeUserPhone) return;
    setDrawerState({
      isOpen: true,
      activeTab: resolveContextDrawerTab(tab),
    });
  }, [activeUserPhone, setDrawerState]);

  const close = useCallback(() => {
    setDrawerState((prev) => ({ ...prev, isOpen: false }));
  }, [setDrawerState]);

  const setTab = useCallback((tab) => {
    setDrawerState((prev) => ({
      ...prev,
      activeTab: resolveContextDrawerTab(tab),
    }));
  }, [setDrawerState]);

  useEffect(() => {
    if (!activeUserPhone) {
      setDrawerState((prev) => (prev.isOpen ? { ...prev, isOpen: false } : prev));
    }
  }, [activeUserPhone, setDrawerState]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [close, isOpen]);

  const isUserListCollapsed = isOpen && isInlineLayout;

  return {
    isOpen,
    activeTab,
    open,
    close,
    setTab,
    isInlineLayout,
    isUserListCollapsed,
  };
}
